// How every screen is laid out. A screen is either a full-screen menu (a top bar, a body that scrolls, maybe a footer
// with the main button) or a full-screen preview (the picture as big as it fits, with a few controls over or under
// it). A menu may start with a small preview, but it scrolls away with the rest of the page.
// Screens opened from another screen go in a Layer: they cover it, and the one underneath keeps its state.
import { createContext, useContext, useLayoutEffect, useRef, useState, type HTMLAttributes, type ReactNode, type Ref } from "react";
import { createPortal } from "react-dom";
import { Icon, OnTop } from "./base";

// A Layer inside another one sits inside it rather than beside it, so an empty outer layer can never end up on top
// of the screen it holds and swallow every tap.
const InLayer = createContext(false);
// A screen opened from inside a page (from its scrolling body, say) goes over the whole page, top bar and footer
// included, not into the body where the page's footer would show through it.
const PageTop = createContext<HTMLElement | null>(null);
export function Layer({ children }: { children: ReactNode }) {
  const top = useContext(PageTop);
  const nested = useContext(InLayer);
  const inner = <InLayer.Provider value={true}><div className="layer">{children}</div></InLayer.Provider>;
  if (top) return createPortal(inner, top);
  return nested ? inner : <OnTop>{inner}</OnTop>;
}

export function Page({ children, dark, className }: { children: ReactNode; dark?: boolean; className?: string }) {
  const [top, setTop] = useState<HTMLDivElement | null>(null);
  return (
    <div className={`page${dark ? " dark" : ""}${className ? ` ${className}` : ""}`}>
      <PageTop.Provider value={top}>{children}</PageTop.Provider>
      <div ref={setTop} className="page-top" />
    </div>
  );
}

export function TopBar({ title, sub, onBack, back = "back", right, children }: {
  title?: ReactNode; sub?: ReactNode; onBack?: () => void; back?: "back" | "close"; right?: ReactNode; children?: ReactNode;
}) {
  return (
    <div className="topbar">
      {onBack ? <button className="icon-btn" onClick={onBack} aria-label={back === "close" ? "Close" : "Back"}><Icon name={back} /></button> : <span className="topbar-space" />}
      {children ?? (
        <div className="topbar-title">
          {title && <b>{title}</b>}
          {sub && <span className="topbar-sub">{sub}</span>}
        </div>
      )}
      <div className="topbar-right">{right ?? <span className="topbar-space" />}</div>
    </div>
  );
}

// `page`: a wizard's current page. When it changes, the body scrolls back to the top.
export function Body({ children, className, page }: { children: ReactNode; className?: string; page?: string | number }) {
  const el = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => { el.current?.scrollTo(0, 0); }, [page]);
  return <div ref={el} className={`page-body${className ? ` ${className}` : ""}`}>{children}</div>;
}

export function Footer({ children }: { children: ReactNode }) {
  return <div className="page-footer">{children}</div>;
}

// The footer of a page opened over another one: one Done button. Everything has already saved, so Done does what back
// does, but it's where the thumb expects it.
export function DoneFooter({ onDone, label = "Done", disabled }: { onDone: () => void; label?: ReactNode; disabled?: boolean }) {
  return <Footer><button className="btn big yellow wide" disabled={disabled} onClick={onDone}><Icon name="check" /> {label}</button></Footer>;
}

export function Section({ title, hint, children, right }: { title?: ReactNode; hint?: ReactNode; children: ReactNode; right?: ReactNode }) {
  return (
    <section className="section">
      {(title || right) && <div className="section-head">{title && <h2>{title}</h2>}{right}</div>}
      {hint && <p className="hint">{hint}</p>}
      {children}
    </section>
  );
}

// A question at the top of a step-by-step screen.
export function Question({ step, children, hint }: { step?: ReactNode; children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="question">
      {step && <span className="step">{step}</span>}
      <h1>{children}</h1>
      {hint && <p className="hint">{hint}</p>}
    </div>
  );
}

// A tappable row that opens another screen.
export function RowLink({ icon, title, sub, onClick, danger, disabled }: { icon?: string; title: ReactNode; sub?: ReactNode; onClick: () => void; danger?: boolean; disabled?: boolean }) {
  return (
    <button className={`row-link${danger ? " danger" : ""}`} onClick={onClick} disabled={disabled}>
      {icon && <span className="row-icon"><Icon name={icon} size={20} /></span>}
      <span className="row-text"><b>{title}</b>{sub && <span>{sub}</span>}</span>
      {!danger && <Icon name="chevron" size={18} />}
    </button>
  );
}

// A 9:16 frame (or another shape, `ratio` = width / height) as big as fits in the space it's given.
export function FitFrame({ children, frameRef, className, ratio = 9 / 16, ...rest }: { children: ReactNode; frameRef?: Ref<HTMLDivElement>; className?: string; ratio?: number } & HTMLAttributes<HTMLDivElement>) {
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const el = box.current!;
    const ro = new ResizeObserver(([e]) => {
      const { width, height } = e!.contentRect;
      const w = Math.floor(Math.min(width, height * ratio));
      setSize(s => (s && s.w === w ? s : { w, h: Math.floor(w / ratio) }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ratio]);
  return (
    <div ref={box} className="fit">
      {size && size.w > 0 && <div ref={frameRef} className={`frame-box${className ? ` ${className}` : ""}`} style={{ width: size.w, height: size.h }} {...rest}>{children}</div>}
    </div>
  );
}
